import moment from 'moment';
import { times } from 'ramda';

moment.updateLocale('en', { week: { dow: 1 } });

const OFFSET_12 = 43200000;
export const ONE_DAY = 86400000;
export const ONE_WEEK = 604800000;
export const VIEW_WEEKLY = 'weekly';
export const VIEW_MONTHLY = 'monthly';

export const getInitDate = () => {
  const now = moment();
  const week = now.week();

  return moment(`${now.year()}-W${week >= 10 ? '' : '0'}${week}`).toDate();
};

export const getInitMonthDate = () => moment().startOf('month').toDate();

const LOCALE = 'de-CH';
const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
const dayFormat = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  firstDay: 1,
  timeZone,
});
const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  timeZone,
});
const monthFormat = new Intl.DateTimeFormat(LOCALE, {
  month: 'long',
  timeZone,
});
const yearFormat = new Intl.DateTimeFormat(LOCALE, {
  year: 'numeric',
  timeZone,
});

export const getDateKey = (date) => moment(date).format('YYYY-MM-DD');

const getNames = (date) => {
  const d = new Date(date);

  d.setMilliseconds(OFFSET_12);

  return {
    weekday: dayFormat.format(d),
    day: dateFormat.format(d),
    month: monthFormat.format(d),
    year: yearFormat.format(d),
  };
};

export const getColumnCount = (showWeekends) => (showWeekends ? 7 : 5);

const isWeekend = (date) => {
  const day = date.getDay();

  return day === 0 || day === 6;
};

const getColumnIndex = (date, showWeekends) => {
  const day = date.getDay();

  if (!showWeekends) {
    if (isWeekend(date)) return -1;

    return day - 1;
  }

  return day === 0 ? 6 : day - 1;
};

const createDay = (date, { index, weekIndex, today, isOutsideMonth = false }) => {
  const key = getDateKey(date);
  const weekend = isWeekend(date);

  return {
    key,
    index,
    weekIndex,
    date,
    ...getNames(date),
    isActive: key === today,
    isOutsideMonth,
    isGray: weekend || isOutsideMonth,
  };
};

export const buildWeekDays = (startDate, showWeekends, today) =>
  times(
    (offset) => createDay(new Date(startDate.valueOf() + offset * ONE_DAY), { index: offset, weekIndex: 0, today }),
    getColumnCount(showWeekends)
  );

export const buildMonthDays = (monthDate, showWeekends, today) => {
  const month = moment(monthDate).startOf('month');
  const gridStart = month.clone().startOf('isoWeek');
  const gridEnd = month.clone().endOf('month').endOf('isoWeek');
  const days = [];
  let weekIndex = 0;
  const current = gridStart.clone();

  while (current.isSameOrBefore(gridEnd, 'day')) {
    const date = current.toDate();
    const columnIndex = getColumnIndex(date, showWeekends);

    if (columnIndex >= 0) {
      days.push(
        createDay(date, {
          index: columnIndex,
          weekIndex,
          today,
          isOutsideMonth: !current.isSame(month, 'month'),
        })
      );
    }

    if (current.isoWeekday() === 7) weekIndex += 1;

    current.add(1, 'day');
  }

  return days;
};

export const buildCalendarDays = ({ startDate, viewMode, showWeekends, today }) => {
  if (viewMode === VIEW_MONTHLY) return buildMonthDays(startDate, showWeekends, today);

  return buildWeekDays(startDate, showWeekends, today);
};

export const groupDaysByWeek = (days) =>
  days.reduce((acc, day) => {
    if (!acc[day.weekIndex]) acc[day.weekIndex] = [];

    acc[day.weekIndex].push(day);

    return acc;
  }, []);

export const getCalendarTitle = (days, viewMode) => {
  if (viewMode === VIEW_MONTHLY) {
    const currentMonthDay = days.find(({ isOutsideMonth }) => !isOutsideMonth) || days[0];

    return `${currentMonthDay.month} ${currentMonthDay.year}`;
  }

  return (
    (days[0].year !== days[days.length - 1].year && [
      days[0].month,
      days[0].year,
      '–',
      days[days.length - 1].month,
      days[days.length - 1].year,
    ]) ||
    (days[0].month !== days[days.length - 1].month && [
      days[0].month,
      '–',
      days[days.length - 1].month,
      days[days.length - 1].year,
    ]) || [days[0].month, days[0].year]
  ).join(' ');
};

export const getWeekdayHeaders = (showWeekends) => {
  const monday = moment().startOf('isoWeek');

  return times((offset) => dayFormat.format(monday.clone().add(offset, 'days').toDate()), getColumnCount(showWeekends));
};

export const isSamePeriod = (date, referenceDate, viewMode) => {
  if (viewMode === VIEW_MONTHLY) {
    return moment(date).isSame(referenceDate, 'month');
  }

  return date.valueOf() === referenceDate.valueOf();
};
